import React, { useState, useEffect, useMemo } from 'react'
import PropTypes from 'prop-types'
import { useNavigate } from 'react-router-dom'
import {
	FileText,
	Download,
	Eye,
	Search,
	ChevronDown,
	ChevronRight,
	User,
	Calendar,
	Clock,
	Shield,
	Folder,
	FolderOpen,
	File,
	Image,
	AlertCircle,
	ExternalLink,
	Filter,
	ArrowUpDown,
	ListTodo,
} from 'lucide-react'
import { Button } from '@components/common'
import projectDocumentService from '@services/projectDocumentService'
import authService from '@services/authService'

/**
 * DocumentsTab Component
 *
 * Displays all documents related to a project in one place, organized as:
 * 1. Project Documents - files uploaded while creating the project
 * 2. Checklist Documents - files uploaded against checklist tasks
 * 3. Checklist-wise Documents - grouped by each checklist/certificate
 */
const DocumentsTab = ({ projectId }) => {
	const navigate = useNavigate()
	const [loading, setLoading] = useState(true)
	const [error, setError] = useState(null)
	const [documentsData, setDocumentsData] = useState(null)
	const [searchQuery, setSearchQuery] = useState('')
	const [expandedGroups, setExpandedGroups] = useState({
		project: true,
		checklist: true,
	})
	const [expandedCertificates, setExpandedCertificates] = useState({})
	const [activeFilter, setActiveFilter] = useState('all')
	const [sortBy, setSortBy] = useState('date_desc')

	// Fetch all documents
	useEffect(() => {
		if (projectId) {
			fetchDocuments()
		}
	}, [projectId])

	const fetchDocuments = async () => {
		setLoading(true)
		setError(null)
		try {
			const response = await projectDocumentService.getAllDocuments(projectId)
			if (response.success || response.data) {
				setDocumentsData(response.data)
				// Expand all certificate groups by default
				const certExpanded = {}
				response.data?.checklist_grouped?.forEach((group, index) => {
					certExpanded[group.certificate_title || index] = true
				})
				setExpandedCertificates(certExpanded)
			} else {
				setError('Failed to load documents')
			}
		} catch (err) {
			console.error('Error fetching documents:', err)
			setError('Failed to load documents. Please try again.')
		} finally {
			setLoading(false)
		}
	}

	// Filter options
	const filterOptions = [
		{ value: 'all', label: 'All Documents' },
		{ value: 'project', label: 'Project Documents' },
		{ value: 'checklist', label: 'Checklist Documents' },
	]

	// Sort options
	const sortOptions = [
		{ value: 'date_desc', label: 'Newest First' },
		{ value: 'date_asc', label: 'Oldest First' },
		{ value: 'name_asc', label: 'Name A-Z' },
		{ value: 'name_desc', label: 'Name Z-A' },
	]

	// Filter and sort documents
	const filteredDocuments = useMemo(() => {
		if (!documentsData) return { project: [], checklist: [], grouped: [] }

		let projectDocs = documentsData.project_documents || []
		let checklistDocs = documentsData.checklist_documents || []

		// Apply search filter
		if (searchQuery.trim()) {
			const query = searchQuery.toLowerCase()
			projectDocs = projectDocs.filter(
				(doc) =>
					doc.original_name?.toLowerCase().includes(query) ||
					doc.document_type?.toLowerCase().includes(query) ||
					`${doc.first_name} ${doc.last_name}`.toLowerCase().includes(query)
			)
			checklistDocs = checklistDocs.filter(
				(doc) =>
					doc.original_name?.toLowerCase().includes(query) ||
					doc.checklist_title?.toLowerCase().includes(query) ||
					doc.certificate_title?.toLowerCase().includes(query) ||
					`${doc.first_name} ${doc.last_name}`.toLowerCase().includes(query)
			)
		}

		// Apply type filter
		if (activeFilter === 'project') {
			checklistDocs = []
		} else if (activeFilter === 'checklist') {
			projectDocs = []
		}

		// Sort documents
		const sortFn = (a, b) => {
			switch (sortBy) {
				case 'date_asc':
					return new Date(a.created_at) - new Date(b.created_at)
				case 'name_asc':
					return (a.original_name || '').localeCompare(b.original_name || '')
				case 'name_desc':
					return (b.original_name || '').localeCompare(a.original_name || '')
				case 'date_desc':
				default:
					return new Date(b.created_at) - new Date(a.created_at)
			}
		}

		projectDocs = [...projectDocs].sort(sortFn)
		checklistDocs = [...checklistDocs].sort(sortFn)

		// Regroup checklist documents
		const grouped = []
		const groupMap = {}
		checklistDocs.forEach((doc) => {
			const groupKey = doc.certificate_title || doc.checklist_title || 'General'
			if (!groupMap[groupKey]) {
				groupMap[groupKey] = {
					certificate_id: doc.certificate_id,
					certificate_title: groupKey,
					documents: [],
				}
				grouped.push(groupMap[groupKey])
			}
			groupMap[groupKey].documents.push(doc)
		})

		return {
			project: projectDocs,
			checklist: checklistDocs,
			grouped,
		}
	}, [documentsData, searchQuery, activeFilter, sortBy])

	// Toggle group expansion
	const toggleGroup = (group) => {
		setExpandedGroups((prev) => ({
			...prev,
			[group]: !prev[group],
		}))
	}

	// Toggle certificate expansion
	const toggleCertificate = (certTitle) => {
		setExpandedCertificates((prev) => ({
			...prev,
			[certTitle]: !prev[certTitle],
		}))
	}

	// Format date
	const formatDate = (dateString) => {
		if (!dateString) return ''
		const date = new Date(dateString)
		return date.toLocaleDateString('en-US', {
			year: 'numeric',
			month: 'short',
			day: 'numeric',
		})
	}

	// Format time
	const formatTime = (dateString) => {
		if (!dateString) return ''
		const date = new Date(dateString)
		return date.toLocaleTimeString('en-US', {
			hour: '2-digit',
			minute: '2-digit',
		})
	}

	// Get file icon based on mime type
	const getFileIcon = (mimeType) => {
		if (mimeType?.startsWith('image/')) {
			return <Image size={18} />
		}
		return <FileText size={18} />
	}

	// Get file type label
	const getFileTypeLabel = (mimeType, documentType) => {
		if (documentType && documentType !== 'other' && documentType !== 'evidence') {
			return projectDocumentService.getTypeLabel(documentType)
		}
		if (mimeType?.includes('pdf')) return 'PDF'
		if (mimeType?.includes('word') || mimeType?.includes('document')) return 'Word Document'
		if (mimeType?.startsWith('image/')) {
			const type = mimeType.split('/')[1]?.toUpperCase()
			return type || 'Image'
		}
		return 'Document'
	}

	// Handle document download
	const handleDownload = async (doc) => {
		try {
			const token = authService.getToken()
			const downloadUrl = projectDocumentService.getDownloadUrl(doc.id)

			const response = await fetch(downloadUrl, {
				headers: {
					Authorization: `Bearer ${token}`,
				},
			})

			if (!response.ok) throw new Error('Download failed')

			const blob = await response.blob()
			const url = window.URL.createObjectURL(blob)
			const a = document.createElement('a')
			a.href = url
			a.download = doc.original_name || 'document'
			document.body.appendChild(a)
			a.click()
			window.URL.revokeObjectURL(url)
			document.body.removeChild(a)
		} catch (err) {
			console.error('Download error:', err)
		}
	}

	// Navigate to task detail
	const handleViewTask = (checklistId) => {
		if (checklistId) {
			navigate(`/tasks/${checklistId}`)
		}
	}

	// Render individual document item
	const renderDocumentItem = (doc, showChecklistInfo = false) => {
		return (
			<div key={doc.id} className="documents-tab__item">
				<div className="documents-tab__item-icon">{getFileIcon(doc.mime_type)}</div>

				<div className="documents-tab__item-info">
					<div className="documents-tab__item-name">{doc.original_name}</div>

					<div className="documents-tab__item-meta">
						<span className="documents-tab__item-type">
							{getFileTypeLabel(doc.mime_type, doc.document_type)}
						</span>
						<span className="documents-tab__item-divider">•</span>
						<span className="documents-tab__item-size">
							{projectDocumentService.formatFileSize(doc.file_size || 0)}
						</span>
					</div>

					<div className="documents-tab__item-details">
						<span className="documents-tab__item-uploader">
							<User size={12} />
							{doc.first_name} {doc.last_name}
						</span>
						<span className="documents-tab__item-date">
							<Calendar size={12} />
							{formatDate(doc.created_at)}
						</span>
						<span className="documents-tab__item-time">
							<Clock size={12} />
							{formatTime(doc.created_at)}
						</span>
					</div>

					{showChecklistInfo && (doc.checklist_title || doc.workflow_step_name) && (
						<div className="documents-tab__item-checklist">
							{doc.checklist_title && (
								<span className="documents-tab__item-checklist-title">
									<ListTodo size={12} />
									{doc.checklist_title}
								</span>
							)}
							{doc.workflow_step_name && (
								<span className="documents-tab__item-workflow-step">
									Step {doc.step_number}: {doc.workflow_step_name}
								</span>
							)}
						</div>
					)}
				</div>

				<div className="documents-tab__item-actions">
					<button
						className="documents-tab__item-action"
						onClick={() => handleDownload(doc)}
						title="Download"
					>
						<Download size={16} />
					</button>
					{showChecklistInfo && doc.checklist_id && (
						<button
							className="documents-tab__item-action documents-tab__item-action--primary"
							onClick={() => handleViewTask(doc.checklist_id)}
							title="View Task"
						>
							<ExternalLink size={16} />
						</button>
					)}
				</div>
			</div>
		)
	}

	// Loading state
	if (loading) {
		return (
			<div className="documents-tab documents-tab--loading">
				<div className="loading-spinner"></div>
				<p>Loading documents...</p>
			</div>
		)
	}

	// Error state
	if (error) {
		return (
			<div className="documents-tab documents-tab--error">
				<AlertCircle size={48} />
				<h4>Error Loading Documents</h4>
				<p>{error}</p>
				<Button variant="outline" size="small" onClick={fetchDocuments}>
					Try Again
				</Button>
			</div>
		)
	}

	const summary = documentsData?.summary || { total: 0, project_count: 0, checklist_count: 0 }
	const totalDocs = filteredDocuments.project.length + filteredDocuments.checklist.length

	return (
		<div className="documents-tab">
			{/* Toolbar */}
			<div className="documents-tab__toolbar">
				<div className="documents-tab__search">
					<Search size={16} />
					<input
						type="text"
						placeholder="Search documents..."
						value={searchQuery}
						onChange={(e) => setSearchQuery(e.target.value)}
					/>
				</div>

				<div className="documents-tab__toolbar-right">
					<div className="documents-tab__filters">
						{filterOptions.map((option) => (
							<button
								key={option.value}
								className={`filter-chip ${activeFilter === option.value ? 'filter-chip--active' : ''}`}
								onClick={() => setActiveFilter(option.value)}
							>
								{option.label}
							</button>
						))}
					</div>

					<div className="documents-tab__sort">
						<select value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
							{sortOptions.map((option) => (
								<option key={option.value} value={option.value}>
									{option.label}
								</option>
							))}
						</select>
						<ArrowUpDown size={14} className="documents-tab__sort-icon" />
					</div>
				</div>
			</div>

			{/* Summary */}
			<div className="documents-tab__summary">
				<div className="documents-tab__summary-stat">
					<span className="documents-tab__summary-value">{totalDocs}</span>
					<span className="documents-tab__summary-label">
						{searchQuery || activeFilter !== 'all' ? 'Matching' : 'Total'} Documents
					</span>
				</div>
				<div className="documents-tab__summary-stat">
					<span className="documents-tab__summary-value">{filteredDocuments.project.length}</span>
					<span className="documents-tab__summary-label">Project Documents</span>
				</div>
				<div className="documents-tab__summary-stat">
					<span className="documents-tab__summary-value">{filteredDocuments.checklist.length}</span>
					<span className="documents-tab__summary-label">Checklist Documents</span>
				</div>
				{filteredDocuments.grouped.length > 0 && (
					<div className="documents-tab__summary-stat">
						<span className="documents-tab__summary-value">{filteredDocuments.grouped.length}</span>
						<span className="documents-tab__summary-label">Standards</span>
					</div>
				)}
			</div>

			{/* Empty state */}
			{totalDocs === 0 && (
				<div className="documents-tab__empty">
					<Folder size={48} />
					<h4>No Documents Found</h4>
					<p>
						{searchQuery || activeFilter !== 'all'
							? 'No documents match your search or filter criteria.'
							: 'No documents have been uploaded to this project yet.'}
					</p>
					{(searchQuery || activeFilter !== 'all') && (
						<Button
							variant="outline"
							size="small"
							onClick={() => {
								setSearchQuery('')
								setActiveFilter('all')
							}}
						>
							Clear Filters
						</Button>
					)}
				</div>
			)}

			{/* Document Groups */}
			{totalDocs > 0 && (
				<div className="documents-tab__groups">
					{/* Project Documents Section */}
					{(activeFilter === 'all' || activeFilter === 'project') &&
						filteredDocuments.project.length > 0 && (
							<div className="documents-tab__group">
								<button
									className="documents-tab__group-header"
									onClick={() => toggleGroup('project')}
								>
									<div className="documents-tab__group-left">
										<span className="documents-tab__group-chevron">
											{expandedGroups.project ? (
												<ChevronDown size={18} />
											) : (
												<ChevronRight size={18} />
											)}
										</span>
										<div className="documents-tab__group-icon documents-tab__group-icon--project">
											<FolderOpen size={18} />
										</div>
										<div className="documents-tab__group-info">
											<span className="documents-tab__group-name">Project Documents</span>
											<span className="documents-tab__group-desc">
												Files uploaded while creating or editing the project
											</span>
										</div>
									</div>
									<div className="documents-tab__group-right">
										<span className="documents-tab__group-count">
											{filteredDocuments.project.length} document
											{filteredDocuments.project.length !== 1 ? 's' : ''}
										</span>
									</div>
								</button>

								{expandedGroups.project && (
									<div className="documents-tab__group-content">
										{filteredDocuments.project.map((doc) => renderDocumentItem(doc, false))}
									</div>
								)}
							</div>
						)}

					{/* Checklist Documents Section - Grouped by Certificate */}
					{(activeFilter === 'all' || activeFilter === 'checklist') &&
						filteredDocuments.grouped.length > 0 && (
							<div className="documents-tab__group">
								<button
									className="documents-tab__group-header"
									onClick={() => toggleGroup('checklist')}
								>
									<div className="documents-tab__group-left">
										<span className="documents-tab__group-chevron">
											{expandedGroups.checklist ? (
												<ChevronDown size={18} />
											) : (
												<ChevronRight size={18} />
											)}
										</span>
										<div className="documents-tab__group-icon documents-tab__group-icon--checklist">
											<FileText size={18} />
										</div>
										<div className="documents-tab__group-info">
											<span className="documents-tab__group-name">Checklist Documents</span>
											<span className="documents-tab__group-desc">
												Files uploaded against checklist tasks
											</span>
										</div>
									</div>
									<div className="documents-tab__group-right">
										<span className="documents-tab__group-count">
											{filteredDocuments.checklist.length} document
											{filteredDocuments.checklist.length !== 1 ? 's' : ''} in{' '}
											{filteredDocuments.grouped.length} standard
											{filteredDocuments.grouped.length !== 1 ? 's' : ''}
										</span>
									</div>
								</button>

								{expandedGroups.checklist && (
									<div className="documents-tab__group-content">
										{filteredDocuments.grouped.map((group) => (
											<div key={group.certificate_title} className="documents-tab__subgroup">
												<button
													className="documents-tab__subgroup-header"
													onClick={() => toggleCertificate(group.certificate_title)}
												>
													<div className="documents-tab__subgroup-left">
														<span className="documents-tab__subgroup-chevron">
															{expandedCertificates[group.certificate_title] ? (
																<ChevronDown size={16} />
															) : (
																<ChevronRight size={16} />
															)}
														</span>
														<div className="documents-tab__subgroup-icon">
															<Shield size={16} />
														</div>
														<span className="documents-tab__subgroup-name">
															{group.certificate_title}
														</span>
													</div>
													<span className="documents-tab__subgroup-count">
														{group.documents.length} document
														{group.documents.length !== 1 ? 's' : ''}
													</span>
												</button>

												{expandedCertificates[group.certificate_title] && (
													<div className="documents-tab__subgroup-content">
														{group.documents.map((doc) => renderDocumentItem(doc, true))}
													</div>
												)}
											</div>
										))}
									</div>
								)}
							</div>
						)}
				</div>
			)}
		</div>
	)
}

DocumentsTab.propTypes = {
	projectId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
}

export default DocumentsTab
